/**
 * Query function wrappers that adapt apiClient methods for TanStack Query.
 *
 * TanStack Query expects queryFn to throw on error or reject the promise.
 * apiClient methods return an envelope: `{ success: boolean, data?: T, error?: string }`.
 * `unwrap()` extracts `data` or throws an Error.
 */

import { apiClient, type ApiResponse, type JobStats, type UserProfile } from '../services/api';
import type { Profile } from '../types/database.types';

export function unwrap<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new Error(response.error || response.message || 'API request failed');
  }
  return response.data as T;
}

// ── Auth & User ──────────────────────────────────────────
export const fetchUserProfile = async (): Promise<UserProfile> =>
  apiClient.getUserProfile().then(unwrap);

export const fetchOnboarding = async (): Promise<any> =>
  apiClient.getOnboarding().then(unwrap);

export const fetchVerificationStatus = async (): Promise<any> =>
  apiClient.getVerificationStatus().then(unwrap);

export const fetchCurrentUser = async (): Promise<any> =>
  apiClient.getCurrentUser().then(unwrap);

// ── Platform ─────────────────────────────────────────────
export const fetchPlatformFeatures = async (): Promise<Array<{ feature_key: string; enabled: boolean }>> =>
  apiClient.getPlatformFeatures().then(unwrap);

// ── Marketplace & Referrers ──────────────────────────────
export const fetchReferrers = async (): Promise<Profile[]> =>
  apiClient.getReferrers().then(unwrap);

export const fetchServiceConfigurationsForService = async (serviceId: string): Promise<any[]> =>
  apiClient.getServiceConfigurationsForService(serviceId).then(unwrap);

export const fetchMyServiceConfigurations = async (): Promise<any[]> =>
  apiClient.getMyServiceConfigurations().then(unwrap);

export const fetchServiceConfigurationsForReferrer = async (referrerId: string): Promise<any[]> =>
  apiClient.getServiceConfigurationsForReferrer(referrerId).then(unwrap);

// ── Requests (Job Seeker Side) ───────────────────────────
export const fetchReferralRequests = async (): Promise<any[]> =>
  apiClient.getReferralRequests().then(unwrap);

export const fetchResumeReviewRequestsForRequester = async (): Promise<any[]> =>
  apiClient.getResumeReviewRequestsForRequester().then(unwrap);

export const fetchMockInterviewRequests = async (): Promise<any[]> =>
  apiClient.getMockInterviewRequests().then(unwrap);

// ── Requests (Referrer Side) ─────────────────────────────
export const fetchReferralRequestsForReferrer = async (): Promise<any[]> =>
  apiClient.getReferralRequestsForReferrer().then(unwrap);

export const fetchResumeReviewRequestsForReferrer = async (): Promise<any[]> =>
  apiClient.getResumeReviewRequestsForReferrer().then(unwrap);

export const fetchMockInterviewRequestsForReferrer = async (): Promise<any[]> =>
  apiClient.getMockInterviewRequestsForReferrer().then(unwrap);

// ── Sessions ─────────────────────────────────────────────
export const fetchReferralSessionsForUser = async (): Promise<any[]> =>
  apiClient.getReferralSessionsForUser().then(unwrap);

export const fetchMockInterviewSessionsForUser = async (): Promise<any[]> =>
  apiClient.getMockInterviewSessionsForUser().then(unwrap);

export const fetchReferralSessionsForReferrer = async (referrerId: string): Promise<any[]> =>
  apiClient.getReferralSessionsForReferrer(referrerId).then(unwrap);

export const fetchMockInterviewSessionsForReferrer = async (referrerId: string): Promise<any[]> =>
  apiClient.getMockInterviewSessionsForReferrer(referrerId).then(unwrap);

export const fetchActiveReservations = async (referrerId: string): Promise<any[]> =>
  apiClient.getActiveReservations(referrerId).then(unwrap);

// ── Jobs ─────────────────────────────────────────────────
export const fetchJobs = async (page = 1, perPage = 20, filters?: Record<string, unknown>): Promise<any> =>
  apiClient.getJobs(page, perPage, filters).then(unwrap);

export const fetchJobStats = async (): Promise<JobStats> =>
  apiClient.getJobStats().then(unwrap);

export const fetchJobById = async (jobId: string | number): Promise<any> =>
  apiClient.getJob(Number(jobId)).then(unwrap);


// ── Signed Storage URLs ──────────────────────────────────
export const fetchSignedResumeUrl = async (path: string): Promise<string | null> => {
  if (!path) return null;
  const res = await apiClient.getSignedResumeUrl(path);
  return res.success && res.data?.signedUrl ? res.data.signedUrl : null;
};
